"use client";

import { Fragment } from "react";
import {
	Button,
	CardCarousel,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps
} from "@pamperedchef/design-system";

import * as styles from "./RecentlyViewedCarousel.module.scss";
import { objectHasValues } from "@/composites/foundation/utilities/objectHasValues";

export interface RecentlyViewedCarouselProduct<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	sku: string;
	image: ImageProps<ImageComponentProps>;
	title: LinkProps<LinkComponentProps>;
	rating?: RatingProps;
	price?: PriceProps;
	isVirtualProduct?: boolean;
}

export interface RecentlyViewedCarouselProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	title: HeadingProps;
	addToCartLabel?: string;
	viewLabel?: string;
	onAddToCart?: (sku: string) => void;
	products: RecentlyViewedCarouselProduct<
		ImageComponentProps,
		LinkComponentProps
	>[];
	/** Suppress the design-system `<Container>` wrapper so the carousel
	 *  inherits its parent's content width. Use when the carousel is
	 *  rendered inside an already-constrained layout (e.g. PDP v2). */
	embedded?: boolean;
	/** Allow slides to bleed past the carousel's right edge toward the
	 *  viewport. Defaults to `true` to preserve legacy full-bleed pages;
	 *  pass `false` when the carousel must align with constrained
	 *  sibling content. */
	allowOverflow?: boolean;
}

export function RecentlyViewedCarousel<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	title,
	addToCartLabel,
	viewLabel,
	onAddToCart,
	products,
	embedded = false,
	allowOverflow = true
}: RecentlyViewedCarouselProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element | null {
	if (!products.length) return null;

	const Wrapper = embedded ? Fragment : Container;

	return (
		<Wrapper>
			<Heading {...title} className={styles["title"]} />
			<CardCarousel
				allowOverflow={allowOverflow}
				classNames={{
					controls: styles["carousel-controls"],
					slides: styles["carousel-slides"],
					prevArrow: styles["carousel-arrow"],
					nextArrow: styles["carousel-arrow"]
				}}
				slides={products.map((product, index) => (
					<article
						key={String(product.title.href)}
						className={styles["product"]}
						aria-labelledby={`rv-product-title-${index}`}
					>
						<Image<ImageComponentProps>
							{...product.image}
							aspectRatio="1:1"
							className={styles["product-image"]}
							alt={product.image.alt || ""}
						/>
						<div className={styles["product-info"]}>
							<Link<LinkComponentProps>
								{...product.title}
								kind="tertiary"
								className={styles["product-title"]}
								id={`rv-product-title-${index}`}
							/>
							{objectHasValues(product.rating) && (
								<Rating {...product.rating} size="small" />
							)}
							{objectHasValues(product.price) && (
								<Price {...product.price} size="small" />
							)}
							{addToCartLabel &&
								(!product.isVirtualProduct ? (
									<Button
										size="sm"
										kind="secondary"
										className={styles["add-to-cart"]}
										onClick={() =>
											onAddToCart?.(product.sku)
										}
									>
										{addToCartLabel}
									</Button>
								) : (
									<Link<LinkComponentProps>
										{...product.title}
										kind="secondary"
										size="sm"
										className={styles["add-to-cart"]}
									>
										{viewLabel}
									</Link>
								))}
						</div>
					</article>
				))}
			/>
		</Wrapper>
	);
}
